import { sanitizePlanRevisionTelemetryEnvelope } from "@arti/plan-revision-telemetry";
import { assertUnreachable } from "@arti/utilities";
import { AnimatePresence, motion } from "framer-motion";
import { type FC, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import type {
  DashboardFlowDetailResponse,
  DashboardPlanSnapshot,
} from "../../dashboard-client/types.gen";
import type {
  ExecutionFlow,
  ExecutionFlowNode,
  PlanRevisionTelemetry,
  TaskSnapshot,
} from "../../types";
import type { StoryFlowLockRecord } from "../../types/storyFlowLocks";

import { useToast } from "../../context/ToastContext";
import {
  getFlowDetailsAsUser,
  getPlanSnapshotByIssueKeyAsUser,
  getPlanRevisionTelemetryAsUser,
  retryAFailedExecutionFlowAsUser,
} from "../../dashboard-client";
import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";
import { useOverlay } from "../../hooks/useOverlay";
import { formatUsd } from "../../utils/currency";
import {
  type FlowCategory,
  FLOW_CATEGORY_CONFIG,
} from "../../utils/taskFlowGrouping";
import {
  getProviderColors,
  getStatusDot,
  getTaskProvider,
} from "../../utils/taskStatus";
import { formatTaskTypeLabel } from "../../utils/taskTypeLabel";
import { formatRelativeTime } from "../../utils/time";
import { ActivePulseIndicator } from "../common/ActivePulseIndicator";
import { HudActionButton } from "../common/HudActionButton";
import { HudPanel } from "../common/HudPanel";
import { HudStatusPill } from "../common/HudStatusPill";
import { LoadingState } from "../common/LoadingState";
import { isKnownProvider, ProviderIcon } from "../common/ProviderIcon";
import { CompileRecordingBody } from "../flows/compileRecording/CompileRecordingBody";
import { CompileRecordingActionCard } from "../flows/CompileRecordingActionCard";
import { ExecutionFlowGraph } from "../flows/ExecutionFlowGraph";
import { ExecutionQaMandateCard } from "../flows/ExecutionQaMandateCard";
import { STATUS_LABELS } from "./constants";
import { PlanRevisionFlowSummary } from "./PlanRevisionFlowSummary";
import { StageJournalPanel } from "./StageJournalPanel";

export interface FlowDetailModalProps {
  flowRunId: string;
  category: FlowCategory;
  ticketId: string | null;
  tasks: TaskSnapshot[];
  lock?: StoryFlowLockRecord | null;
  onClearLock?: (storyIssueKey: string) => void;
  onClose: () => void;
  selectedNodeId?: string | null;
  onSelectNodeId?: (nodeId: string | null) => void;
}

/**
 * Strip the category prefix (e.g. `exec:`, `rev:`, `run:`) from a
 * `flow_run_id` to obtain the raw identifier expected by API endpoints.
 */
export function stripFlowPrefix(flowRunId: string): string {
  const idx = flowRunId.indexOf(":");
  return idx !== -1 ? flowRunId.slice(idx + 1) : flowRunId;
}

type ExecutionFlowDetailResponse = Extract<
  DashboardFlowDetailResponse,
  { flow_type: "execution" }
>;

function toExecutionFlowDetail(detail: ExecutionFlowDetailResponse): {
  compileRecordingRequestIds: string[];
  compileRecordingEnabled: boolean;
  flow: ExecutionFlow;
  nodes: ExecutionFlowNode[];
} {
  return {
    compileRecordingEnabled: detail.feature_flags.compile_recording,
    compileRecordingRequestIds: detail.compile_recording_request_ids,
    flow: detail.flow,
    nodes: detail.nodes.map((node) => {
      const planItemId = node.plan_item_id ?? node.ticket_id ?? node.id;
      const ticketId = node.ticket_id ?? node.plan_item_id ?? node.id;

      return {
        ...node,
        plan_item_id: planItemId,
        repository_slug: node.repository_slug ?? "",
        review_feedback: node.review_feedback.map((feedback) => ({
          ...feedback,
          normalized_filepath: feedback.normalized_filepath ?? null,
        })),
        ticket_id: ticketId,
      };
    }),
  };
}

function assertExecutionFlowDetail(
  detail: DashboardFlowDetailResponse,
): ExecutionFlowDetailResponse {
  if (detail.flow_type !== "execution") {
    throw new Error("Expected execution flow detail.");
  }
  return detail;
}

const ServerIcon: FC = () => (
  <svg
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
    />
  </svg>
);

const FlowTaskRow: FC<{ task: TaskSnapshot }> = ({ task }) => {
  const dot = getStatusDot(task.status);
  const rawProvider = getTaskProvider(task);
  const provider =
    rawProvider && isKnownProvider(rawProvider) ? rawProvider : null;
  const providerColors = getProviderColors(rawProvider);

  const statusIndicator = dot.pulse ? (
    <ActivePulseIndicator
      size={provider ? "md" : "sm"}
      className="flex-shrink-0"
      colorClassName={providerColors.pulseColor}
      radarClassName={providerColors.radarColor}
    >
      {provider && (
        <ProviderIcon provider={provider} className="relative h-full w-full" />
      )}
    </ActivePulseIndicator>
  ) : (
    <span
      aria-hidden="true"
      className={`inline-flex h-2 w-2 flex-shrink-0 rounded-none ${dot.color}`}
    />
  );

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.02]">
      {statusIndicator}
      <div className="min-w-0 flex-1">
        <div className="truncate font-mono text-[11px] uppercase tracking-[0.18em] text-slate-800 dark:text-slate-100">
          {formatTaskTypeLabel(task.type)}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
          <span className="font-bold text-slate-500 dark:text-slate-400">
            {STATUS_LABELS[task.status]}
          </span>
          {task.assigned_node && (
            <span
              className="hidden items-center gap-1 sm:flex"
              title={`Node: ${task.assigned_node}`}
            >
              <ServerIcon />
              <span className="max-w-[140px] truncate normal-case tracking-normal">
                {task.assigned_node}
              </span>
            </span>
          )}
        </div>
      </div>
      {task.cost_usd != null && task.cost_usd > 0 && (
        <HudStatusPill
          label={formatUsd(task.cost_usd)}
          tone="success"
          hideDot
        />
      )}
      {task.attempt_count > 1 && (
        <span title={`${task.attempt_count} attempts`}>
          <HudStatusPill
            label={`${task.attempt_count}x`}
            tone="warning"
            hideDot
          />
        </span>
      )}
      <span className="hidden flex-shrink-0 whitespace-nowrap text-xs text-slate-400 sm:inline">
        {formatRelativeTime(task.updated_at)}
      </span>
    </div>
  );
};

export const FlowTasksList: FC<{ tasks: TaskSnapshot[] }> = ({ tasks }) => (
  <HudPanel
    title="Tasks"
    actions={
      <HudStatusPill label={String(tasks.length)} tone="neutral" hideDot />
    }
    bodyClassName=""
  >
    {tasks.length === 0 ? (
      <p className="py-6 text-center font-mono text-[11px] uppercase tracking-widest text-slate-400 dark:text-slate-500">
        No tasks associated with this flow.
      </p>
    ) : (
      <div className="divide-y divide-slate-100 dark:divide-white/5">
        {tasks.map((task) => (
          <FlowTaskRow key={task.task_id} task={task} />
        ))}
      </div>
    )}
  </HudPanel>
);

const FlowFailureBanner: FC<{ flow: ExecutionFlow }> = ({ flow }) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const toast = useToast();

  if (flow.status !== "failed") {
    return null;
  }

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      const { data } = await retryAFailedExecutionFlowAsUser({
        path: { flowId: flow.id },
      });
      if (data.status === "started" && data.request_id) {
        toast.present({
          body: `Started execution ${data.request_id}. It will appear as a new flow in the dashboard.`,
          title: "Execution retry started",
          tone: "success",
        });
        return;
      }
      toast.present({
        body: "All remaining plan items are already done for this revision.",
        title: "Nothing left to retry",
        tone: "info",
      });
    } catch (error) {
      toast.present({
        body:
          error instanceof Error
            ? error.message
            : "Failed to retry execution flow.",
        title: "Execution retry failed",
        tone: "error",
      });
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 border border-rose-500/40 bg-rose-500/5 p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <HudStatusPill label="Execution Failed" tone="danger" />
        <HudActionButton
          label="Retry Execution"
          tone="danger"
          loading={isRetrying}
          onClick={() => void handleRetry()}
        />
      </div>
      <p className="font-mono text-[11px] text-rose-700 dark:text-rose-300">
        {flow.failure_reason?.trim() || "The last execution attempt failed."}
      </p>
    </div>
  );
};

const FlowLockBanner: FC<{
  lock: StoryFlowLockRecord;
  onClearLock?: (storyIssueKey: string) => void;
}> = ({ lock, onClearLock }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border border-amber-500/40 bg-amber-500/5 p-3">
    <div className="flex flex-wrap items-center gap-2">
      <HudStatusPill label="Locked" tone="warning" pulse />
      <span className="font-mono text-[11px] uppercase tracking-widest text-amber-700 dark:text-amber-300">
        {lock.lockOwner}
      </span>
      {lock.activeFlowType && (
        <span className="font-mono text-[10px] uppercase tracking-widest text-amber-600/70 dark:text-amber-400/60">
          {lock.activeFlowType}
        </span>
      )}
    </div>
    {onClearLock && (
      <HudActionButton
        label="Release Lock"
        tone="neutral"
        onClick={() => onClearLock(lock.storyIssueKey)}
      />
    )}
  </div>
);

const ExecutionFlowBody: FC<{
  flowRunId: string;
  tasks: TaskSnapshot[];
  lock?: StoryFlowLockRecord | null;
  onClearLock?: (storyIssueKey: string) => void;
  selectedNodeId?: string | null;
  onSelectNodeId?: (nodeId: string | null) => void;
}> = ({
  flowRunId,
  tasks,
  lock,
  onClearLock,
  selectedNodeId,
  onSelectNodeId,
}) => {
  const [flowDetail, setFlowDetail] = useState<{
    compileRecordingRequestIds: string[];
    compileRecordingEnabled: boolean;
    flow: ExecutionFlow;
    nodes: ExecutionFlowNode[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { subscribe } = useDashboardRealtime();

  const rawId = stripFlowPrefix(flowRunId);

  const fetchFlowDetail = useCallback(async () => {
    const { data } = await getFlowDetailsAsUser({ path: { flowId: rawId } });
    if (!data) {
      throw new Error("Flow detail response is empty.");
    }
    return toExecutionFlowDetail(assertExecutionFlowDetail(data));
  }, [rawId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchFlowDetail()
      .then((data) => {
        if (!cancelled) {
          setFlowDetail(data);
        }
      })
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Failed to load flow.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fetchFlowDetail]);

  useEffect(() => {
    let timeout: number | null = null;
    const scheduleRefresh = () => {
      if (timeout !== null) {
        return;
      }
      timeout = window.setTimeout(() => {
        timeout = null;
        fetchFlowDetail()
          .then(setFlowDetail)
          .catch(() => {
            /* Keep existing data on background refresh failure */
          });
      }, 500);
    };

    const unsubscribe = subscribe(
      (message) => {
        let executionRequestId: string | null = null;
        switch (message.type) {
          case "compile_recording_snapshot":
          case "execution_node_snapshot":
          case "execution_request_snapshot":
          case "provider_progress":
          case "queue_delta": {
            executionRequestId = message.payload.execution_request_id ?? null;
            break;
          }
          default: {
            executionRequestId = null;
          }
        }
        if (executionRequestId !== rawId) {
          return;
        }
        scheduleRefresh();
      },
      { topic: "internalEventTopic" },
    );

    return () => {
      unsubscribe();
      if (timeout !== null) {
        window.clearTimeout(timeout);
      }
    };
  }, [rawId, fetchFlowDetail, subscribe]);

  if (loading) {
    return <LoadingState message="Loading execution flow..." />;
  }
  if (error) {
    return (
      <div className="border border-rose-500/40 bg-rose-500/5 p-3">
        <HudStatusPill label="Load Failed" tone="danger" />
        <p className="mt-2 font-mono text-[11px] text-rose-700 dark:text-rose-300">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {flowDetail ? <FlowFailureBanner flow={flowDetail.flow} /> : null}
      {flowDetail ? (
        <CompileRecordingActionCard
          compileRecordingEnabled={flowDetail.compileRecordingEnabled}
          flow={flowDetail.flow}
          compileRecordingRequestIds={flowDetail.compileRecordingRequestIds}
        />
      ) : null}

      {lock?.lockOwner && (
        <FlowLockBanner lock={lock} onClearLock={onClearLock} />
      )}

      {flowDetail && flowDetail.nodes.length > 0 && (
        <ExecutionFlowGraph
          nodes={flowDetail.nodes}
          storyKey={flowDetail.flow.issue_key}
          branchName={flowDetail.flow.branch_name}
          ticketDetailsClassName="space-y-4"
          selectedNodeId={selectedNodeId}
          onSelectedNodeIdChange={onSelectNodeId}
        />
      )}

      {flowDetail?.flow.qa_mandate ? (
        <ExecutionQaMandateCard qaMandate={flowDetail.flow.qa_mandate} />
      ) : null}

      <StageJournalPanel flowId={rawId} flowType="execution" />

      <FlowTasksList tasks={tasks} />
    </div>
  );
};

const PlanRevisionBody: FC<{
  flowRunId: string;
  tasks: TaskSnapshot[];
  issueKey: string | null;
}> = ({ flowRunId, tasks, issueKey }) => {
  const rawId = stripFlowPrefix(flowRunId);
  const [telemetryState, setTelemetryState] = useState<{
    status: "idle" | "loading" | "loaded" | "error";
    data: PlanRevisionTelemetry | null;
  }>({ data: null, status: "idle" });
  const [planSnapshot, setPlanSnapshot] =
    useState<DashboardPlanSnapshot | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTelemetryState((prev) => ({ ...prev, status: "loading" }));
    getPlanRevisionTelemetryAsUser({ path: { revisionId: rawId } })
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        const response = sanitizePlanRevisionTelemetryEnvelope(data);
        setTelemetryState({
          data: response.telemetry ?? null,
          status: "loaded",
        });
      })
      .catch(() => {
        if (!cancelled) {
          setTelemetryState({ data: null, status: "error" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rawId]);

  useEffect(() => {
    if (!issueKey) {
      return;
    }
    let cancelled = false;
    getPlanSnapshotByIssueKeyAsUser({
      path: { issueKey },
      query: { include_tree: "true" },
    })
      .then(({ data }) => {
        if (!cancelled && data) {
          setPlanSnapshot(data.snapshot);
        }
      })
      .catch(() => {
        /* Plan snapshot is supplementary — fail silently */
      });
    return () => {
      cancelled = true;
    };
  }, [issueKey]);

  return (
    <div className="space-y-5">
      <PlanRevisionFlowSummary
        tasks={tasks}
        telemetry={telemetryState.data}
        telemetryStatus={telemetryState.status}
        planSnapshot={planSnapshot}
      />

      <StageJournalPanel flowId={rawId} flowType="plan_revision" />

      <FlowTasksList tasks={tasks} />
    </div>
  );
};

const FlowModalBodyByCategory: FC<{
  category: FlowCategory;
  flowRunId: string;
  ticketId: string | null;
  tasks: TaskSnapshot[];
  lock?: StoryFlowLockRecord | null;
  onClearLock?: (storyIssueKey: string) => void;
  selectedNodeId?: string | null;
  onSelectNodeId?: (nodeId: string | null) => void;
}> = ({
  category,
  flowRunId,
  ticketId,
  tasks,
  lock,
  onClearLock,
  selectedNodeId,
  onSelectNodeId,
}) => {
  switch (category) {
    case "execution": {
      return (
        <ExecutionFlowBody
          flowRunId={flowRunId}
          tasks={tasks}
          lock={lock}
          onClearLock={onClearLock}
          selectedNodeId={selectedNodeId}
          onSelectNodeId={onSelectNodeId}
        />
      );
    }
    case "plan_revision": {
      return (
        <PlanRevisionBody
          flowRunId={flowRunId}
          tasks={tasks}
          issueKey={ticketId}
        />
      );
    }
    case "compile_recording": {
      return <CompileRecordingBody flowRunId={flowRunId} tasks={tasks} />;
    }
    case "source_control":
    case "analysis":
    case "actor_instance": {
      return <FlowTasksList tasks={tasks} />;
    }
    default: {
      throw assertUnreachable(category);
    }
  }
};

const overlayMotion = {
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  initial: { opacity: 0 },
  transition: { duration: 0.15 },
};

const panelMotion = {
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.97, y: 8 },
  initial: { opacity: 0, scale: 0.97, y: 8 },
  transition: {
    damping: 22,
    mass: 0.8,
    stiffness: 260,
    type: "spring" as const,
  },
};

const ModalCornerBracket: FC<{ position: "tl" | "tr" | "bl" | "br" }> = ({
  position,
}) => {
  const map: Record<typeof position, string> = {
    bl: "bottom-1 left-1 border-b border-l",
    br: "bottom-1 right-1 border-b border-r",
    tl: "top-1 left-1 border-l border-t",
    tr: "top-1 right-1 border-r border-t",
  };
  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute z-10 h-2 w-2 border-emerald-400/60 ${map[position]}`}
    />
  );
};

export const FlowDetailModal: FC<FlowDetailModalProps> = ({
  flowRunId,
  category,
  ticketId,
  tasks,
  lock,
  onClearLock,
  onClose,
  selectedNodeId,
  onSelectNodeId,
}) => {
  useOverlay(true, onClose);
  const config = FLOW_CATEGORY_CONFIG[category];
  const headerLabel = `${config.label} Flow${ticketId ? ` — ${ticketId}` : ""}`;

  return createPortal(
    <AnimatePresence>
      <motion.div
        {...overlayMotion}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 backdrop-blur-sm md:p-6"
        onClick={onClose}
      >
        <motion.div
          {...panelMotion}
          onClick={(e) => e.stopPropagation()}
          className="relative flex h-full max-h-[100vh] w-full max-w-6xl flex-col overflow-hidden border border-emerald-400/30 bg-slate-950 shadow-[0_0_0_1px_rgba(16,185,129,0.08),0_40px_80px_-20px_rgba(16,185,129,0.35)] md:h-auto md:max-h-[88vh]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(16,185,129,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.04) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        >
          <ModalCornerBracket position="tl" />
          <ModalCornerBracket position="tr" />
          <ModalCornerBracket position="bl" />
          <ModalCornerBracket position="br" />

          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, rgba(16,185,129,1) 0 1px, transparent 1px 3px)",
            }}
          />

          <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent" />

          <div className="relative flex flex-shrink-0 items-start justify-between gap-3 border-b border-emerald-400/15 px-5 py-3 md:px-6 md:py-4">
            <div className="min-w-0">
              <p className="truncate font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-400/70">
                [ {headerLabel.toUpperCase()} ]
              </p>
              <p className="mt-1 truncate font-mono text-[11px] tracking-tight text-slate-500">
                {flowRunId}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center border border-emerald-400/30 font-mono text-[14px] text-emerald-300/80 transition-colors hover:border-emerald-400/60 hover:text-emerald-300"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="relative flex-1 overflow-y-auto px-5 py-5 md:px-6">
            <FlowModalBodyByCategory
              category={category}
              flowRunId={flowRunId}
              ticketId={ticketId}
              tasks={tasks}
              lock={lock}
              onClearLock={onClearLock}
              selectedNodeId={selectedNodeId}
              onSelectNodeId={onSelectNodeId}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};
